if (!customElements.get('back-in-stock-form')) {
  class BackInStockForm extends HTMLElement {
    connectedCallback() {
      this.variantInput = this.querySelector('.js-back-in-stock-variant');
      this.skuInput = this.querySelector('.js-back-in-stock-sku');
      this.urlInput = this.querySelector('.js-back-in-stock-url');
      this.messageEl = this.querySelector('.js-back-in-stock-message');
      this.form = this.querySelector('form');
      this.emailInput = this.querySelector('input[type="email"]');
      this.productUrl = this.dataset.productUrl || '';
      this.variantId = this.dataset.variantId || '';
      this.klaviyoKey = this.dataset.klaviyoKey || '';

      const productInfo = this.closest('.product-info');
      this.variantPicker = productInfo ? productInfo.querySelector('variant-picker') : null;

      if (this.variantPicker) {
        this.variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Only take over the submit when Klaviyo is configured. Without a key the native
      // contact form posts as usual and the store receives the request by email.
      if (this.klaviyoKey && this.form) {
        this.boundHandleSubmit = this.handleSubmit.bind(this);
        this.form.addEventListener('submit', this.boundHandleSubmit);
      }
    }

    /**
     * Keeps the hidden form fields and the tracked variant in sync with the selection.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      const { variant } = evt.detail;

      this.variantId = variant ? `${variant.id}` : '';

      if (this.variantInput) {
        this.variantInput.value = variant && variant.title ? variant.title : '';
      }

      if (this.skuInput) {
        this.skuInput.value = variant && variant.sku ? variant.sku : '';
      }

      if (this.urlInput) {
        this.urlInput.value = variant
          ? `${this.productUrl}?variant=${variant.id}`
          : this.productUrl;
      }

      this.hideMessage();
    }

    /**
     * Subscribes the customer in Klaviyo instead of posting the contact form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      const email = this.emailInput ? this.emailInput.value.trim() : '';

      // Let the browser handle empty or malformed addresses through native validation,
      // and fall back to the contact form when there is no variant to subscribe to.
      if (!email || !this.emailInput.checkValidity() || !this.variantId) return;

      evt.preventDefault();

      const button = this.form.querySelector('[type="submit"]');
      if (button) button.setAttribute('disabled', '');
      this.hideMessage();

      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(this.klaviyoKey)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              revision: '2026-07-15'
            },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  channels: ['EMAIL'],
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: { email }
                    }
                  }
                },
                relationships: {
                  variant: {
                    data: {
                      type: 'catalog-variant',
                      id: `$shopify:::$default:::${this.variantId}`
                    }
                  }
                }
              }
            })
          }
        );

        if (!response.ok) throw new Error(`Klaviyo responded with ${response.status}`);

        this.form.reset();
        this.showMessage(this.dataset.successText, true);
      } catch (error) {
        console.error(error);

        // Klaviyo is unreachable or rejected the request, so the next submit posts the contact
        // form after all rather than losing the signup. Removing the listener prevents a loop.
        this.form.removeEventListener('submit', this.boundHandleSubmit);
        this.showMessage(this.dataset.errorText, false);
      } finally {
        if (button) button.removeAttribute('disabled');
      }
    }

    /**
     * Shows an inline status message.
     * @param {string} text - Message to show.
     * @param {boolean} success - Whether the message reports success.
     */
    showMessage(text, success) {
      if (!this.messageEl || !text) return;

      this.messageEl.textContent = text;
      this.messageEl.className = `back-in-stock__message js-back-in-stock-message alert ${
        success ? 'bg-success-bg text-success-text' : 'bg-error-bg text-error-text'
      }`;
      this.messageEl.hidden = false;
    }

    hideMessage() {
      if (!this.messageEl) return;

      this.messageEl.hidden = true;
      this.messageEl.textContent = '';
    }
  }

  customElements.define('back-in-stock-form', BackInStockForm);
}
